import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Accordion from '@/components/base/Accordion';
import { useSEO } from '@/hooks/useSEO';
import { faqItems } from '@/mocks/faq';

const HELP_LINKS = [
  { icon: 'ri-compass-3-line', label: '浏览话题库', to: '/topics', text: '按栏目查看全部话题与实时更新' },
  { icon: 'ri-information-line', label: '了解内容原则', to: '/about', text: '看看我们如何核实与更正内容' },
  { icon: 'ri-send-plane-line', label: '直接联系我们', to: '/contact', text: '提供线索、申请更正或反馈问题' },
];

export default function Faq() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin || undefined },
          { '@type': 'ListItem', position: 2, name: '常见问题' },
        ],
      },
    ];
  }, []);

  useSEO({
    title: '常见问题 - 内容来源、更新与更正说明 | 黑料首页',
    description:
      '关于黑料首页的常见问题：内容来源、话题为何多次更新、如何申请更正、怎样第一时间看到上新内容、热度数值的含义，以及如何提供线索。',
    keywords: '常见问题,黑料首页,内容来源,内容更正,热度说明',
    canonicalPath: '/faq',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              常见问题
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-700">
              我们把读者问得最多的几个问题整理在这里。如果下面没有覆盖到你的疑问，欢迎直接来信，编辑部会在值守时间内回复。
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.7fr_1.3fr]">
            <aside className="lg:sticky lg:top-32 lg:self-start">
              <h2 className="font-heading text-xl font-bold text-foreground-950">还没找到答案？</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                试试从下面几个入口继续探索，或直接把问题发给我们。
              </p>
              <ul className="mt-5 flex flex-col gap-3">
                {HELP_LINKS.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                        <i className={item.icon} aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-950">{item.label}</span>
                        <span className="mt-0.5 block text-xs text-foreground-600">{item.text}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>

            <div>
              <Accordion items={faqItems} defaultOpenId="faq-1" />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}